/**
 * Usar um modelo pronto: o painel lateral com a configuração dele. Com
 * `edicao`, o mesmo painel abre a automação já criada, com a configuração dela,
 * e salvar troca a configuração (horários, consulta, ação e os dados dela).
 *
 * O protótipo criava o rascunho direto, com valores de exemplo. O serviço não
 * aceita um modelo sem configuração (o prazo, o expediente, a operação que ele
 * lê), então o "Usar" abre este painel: a pessoa diz o que o modelo precisa, e
 * a automação nasce como rascunho, que ainda não roda.
 */

import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ErroDaApi, type AutomacaoDaApi } from '@cliqcrm/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { chaves } from '@/shared/api/chaves';
import {
  useApi,
  useBlocosDoEditor,
  useCaixasDoAtendimento,
  useOperacoesDeDados,
} from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Campo, CampoArea, CampoDeDuracao, CampoSelecao, Dialogo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  DIAS_DA_SEMANA,
  FUSOS,
  MAXIMO_DE_OPCOES,
  MODELO_LEMBRETE,
  MODELO_MENU,
  MODELO_REDISTRIBUIR,
  MODELO_RESPONSAVEL,
  MODELO_ROTINA,
  agendaInicial,
  configuracaoLembrete,
  configuracaoMenu,
  configuracaoRedistribuir,
  configuracaoRotina,
  formularioLembreteDe,
  formularioMenuInicial,
  formularioRedistribuirInicial,
  formularioRotinaDe,
  opcaoVazia,
  problemasLembrete,
  problemasMenu,
  problemasRedistribuir,
  problemasRotina,
  restoDaConfiguracao,
  type FormularioAgenda,
  type FormularioLembrete,
  type FormularioMenu,
  type FormularioRedistribuir,
  type FormularioRotina,
  type Modelo,
  type ParametroDaAcao,
  type Problemas,
} from '../lib/modelos';
import { DadoDaAcao, ParametrosDaRotina, type ReferenciaDoMenu } from './acao-da-rotina';
import './configurar-modelo.css';

/** O que a ação de uma opção do menu usa: a conversa de quem escolheu. */
const DO_MENU: readonly ReferenciaDoMenu[] = [
  { tipo: 'conversa', caminho: 'conversation_id', titulo: 'A conversa de quem escolheu' },
];

const ID_DO_FORMULARIO = 'configurar-modelo';

/** Os modelos guardam minutos inteiros: o campo de tempo mostra e devolve em ms. */
function msDosMinutos(minutos: string): number | null {
  const n = Number(minutos);
  return Number.isFinite(n) && n > 0 ? n * 60_000 : null;
}

/** O minuto seguinte quando sobra segundo: 00:01:30 vale 2 minutos, não 1. */
function minutosDoMs(ms: number): string {
  return ms > 0 ? String(Math.ceil(ms / 60_000)) : '';
}

export function ConfigurarModelo({
  modelo,
  edicao,
  aoFechar,
  aoCriado,
  aoSalvo,
}: {
  modelo: Modelo;
  /** A automação que se edita; sem ela, o painel cria uma nova. */
  edicao?: AutomacaoDaApi;
  aoFechar: () => void;
  aoCriado?: (nome: string) => void;
  aoSalvo?: (automacao: AutomacaoDaApi) => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const blocos = useBlocosDoEditor();
  /*
   * A chave é do PEDIDO: o duplo clique e a nova tentativa depois de uma queda
   * de rede reusam a mesma, e não criam duas automações. Mudou o que vai ser
   * enviado, é outro pedido e outra chave; senão o servidor recusaria o corpo
   * novo com a chave antiga.
   */
  const tentativa = useRef<{ corpo: string; chave: string } | null>(null);
  const chavePara = (corpo: unknown): string => {
    const texto = JSON.stringify(corpo);
    if (tentativa.current?.corpo !== texto) {
      tentativa.current = { corpo: texto, chave: crypto.randomUUID() };
    }
    return tentativa.current.chave;
  };

  /*
   * Editando, a configuração é a da versão que se edita: a em teste, quando há.
   * Ela e a versão ficam como estavam ao abrir, junto com o formulário: a
   * releitura em segundo plano traria a versão de outra pessoa, e o salvar
   * passaria por cima dela sem o 409.
   */
  const [lida] = useState(() =>
    edicao
      ? {
          salva: (edicao.pendente ?? edicao).configuracao_do_modelo?.configuracao ?? {},
          versao: edicao.pendente?.versao ?? edicao.versao,
        }
      : null,
  );
  const salva = lida?.salva ?? null;
  // A configuração já gravada quando o nome falha depois dela.
  const [soONomeFalhou, definirSoONomeFalhou] = useState(false);
  const [nome, definirNome] = useState(edicao?.nome ?? '');
  const [redistribuir, definirRedistribuir] = useState<FormularioRedistribuir>(
    formularioRedistribuirInicial,
  );
  const [lembrete, definirLembrete] = useState<FormularioLembrete>(() =>
    salva && modelo.chave === MODELO_LEMBRETE
      ? formularioLembreteDe(salva)
      : { agenda: agendaInicial(), operacaoId: '', modeloDoLembrete: '' },
  );
  const [rotina, definirRotina] = useState<FormularioRotina>(() =>
    salva && modelo.chave === MODELO_ROTINA
      ? formularioRotinaDe(salva)
      : {
          agenda: agendaInicial(),
          operacaoId: '',
          parametrosDaOperacao: [],
          acao: '',
          parametros: {},
        },
  );
  // A consulta da rotina: os parâmetros que ela pede e as colunas que cada linha traz.
  // Uma leitura só das consultas, e só nos modelos que leem o banco: para quem não
  // é da Cliqfy, a lista responderia 404.
  const consultas = useOperacoesDeDados(
    'consulta',
    modelo.chave === MODELO_ROTINA || modelo.chave === MODELO_LEMBRETE,
  );
  const consultaDaRotina = consultas.data?.itens.find((o) => o.id === rotina.operacaoId);
  const colunasDaRotina = (consultaDaRotina?.colunas ?? []).map((c) => c.nome);
  const [menu, definirMenu] = useState<FormularioMenu>(formularioMenuInicial);
  const [problemas, definirProblemas] = useState<Problemas>({});

  const acoes = blocos.data?.acoes ?? [];
  const parametrosDe = (acao: string): readonly ParametroDaAcao[] =>
    acoes.find((a) => a.acao === acao)?.parametros ?? [];
  const parametrosDaAcao = parametrosDe(rotina.acao);

  const criar = useMutation({
    mutationFn: (configuracao: Record<string, unknown>) => {
      const corpo = {
        modelo: modelo.chave,
        configuracao,
        ...(nome.trim() ? { nome: nome.trim() } : {}),
      };
      return api!.criarAutomacaoDoModelo({ corpo, idempotencyKey: chavePara(corpo) });
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.automacoes() });
      aoCriado?.(nome.trim() || modelo.nome);
    },
    // O que o serviço recusou volta para o campo, quando ele tem campo aqui.
    onError: (erro) => {
      if (erro instanceof ErroDaApi) definirProblemas(problemasDaApi(erro));
    },
  });

  /*
   * Editar: a configuração inteira vai de novo, com o que o formulário não
   * mostra (o resto da salva), na versão lida. O nome é à parte: não cria versão.
   */
  const editar = useMutation({
    mutationFn: async (configuracao: Record<string, unknown>) => {
      const id = edicao!.id;
      definirSoONomeFalhou(false);
      const consulta = String(configuracao['operacao_id'] ?? '');
      const resposta = await api!.editarAutomacaoDoModelo({
        caminho: { id },
        corpo: {
          configuracao: {
            ...configuracao,
            ...restoDaConfiguracao(modelo.chave, salva ?? {}, consulta),
          },
          versao: lida!.versao,
        },
      });
      if (!nome.trim() || nome.trim() === (edicao!.nome ?? '')) return resposta;
      try {
        return await api!.identificarAutomacao({ caminho: { id }, corpo: { nome: nome.trim() } });
      } catch (erro) {
        // A configuração já está gravada: a tela a relê e diz que só o nome ficou.
        cache.setQueryData(chaves.automacao(id), resposta);
        void cache.invalidateQueries({ queryKey: chaves.automacoes() });
        definirSoONomeFalhou(true);
        throw erro;
      }
    },
    onSuccess: (resposta) => {
      cache.setQueryData(chaves.automacao(resposta.id), resposta);
      void cache.invalidateQueries({ queryKey: chaves.automacoes() });
      void cache.invalidateQueries({ queryKey: chaves.historicoDaAutomacao(resposta.id) });
      aoSalvo?.(resposta);
    },
    onError: (erro) => {
      if (erro instanceof ErroDaApi) definirProblemas(problemasDaApi(erro));
    },
  });
  const envio = edicao ? editar : criar;

  const enviar = (evento: FormEvent): void => {
    evento.preventDefault();
    if (envio.isPending) return;
    const [achados, configuracao]: [Problemas, Record<string, unknown>] =
      modelo.chave === MODELO_REDISTRIBUIR
        ? [problemasRedistribuir(redistribuir), configuracaoRedistribuir(redistribuir)]
        : modelo.chave === MODELO_LEMBRETE
          ? [problemasLembrete(lembrete), configuracaoLembrete(lembrete)]
          : modelo.chave === MODELO_MENU
            ? [problemasMenu(menu, parametrosDe), configuracaoMenu(menu)]
            : modelo.chave === MODELO_RESPONSAVEL
              ? [{}, {}]
              : [problemasRotina(rotina, parametrosDaAcao), configuracaoRotina(rotina)];
    definirProblemas(achados);
    if (Object.keys(achados).length > 0) return;
    envio.mutate(configuracao);
  };

  const quantos = Object.keys(problemas).length;

  return (
    <Dialogo
      titulo={edicao ? `Editar “${edicao.nome?.trim() || modelo.nome}”` : `Usar “${modelo.nome}”`}
      forma="lateral"
      aoFechar={() => {
        if (!envio.isPending) aoFechar();
      }}
      botoes={
        <>
          <Botao
            variante="fantasma"
            onClick={aoFechar}
            desabilitado={envio.isPending}
            motivo={edicao ? 'Salvando…' : 'Criando…'}
          >
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form={ID_DO_FORMULARIO}
            variante="primario"
            desabilitado={envio.isPending || api === null}
            motivo={
              envio.isPending ? (edicao ? 'Salvando…' : 'Criando…') : 'Sem sessão: entre de novo.'
            }
          >
            {edicao
              ? editar.isPending
                ? 'Salvando…'
                : 'Salvar'
              : criar.isPending
                ? 'Criando…'
                : 'Criar rascunho'}
          </Botao>
        </>
      }
    >
      <form id={ID_DO_FORMULARIO} className="configurar-modelo" onSubmit={enviar} noValidate>
        <p className="configurar-modelo__apoio">{modelo.descricao}</p>
        <p className="configurar-modelo__regra">
          {!edicao
            ? 'Ela nasce como rascunho e não roda até alguém mandar para teste e ativar.'
            : edicao.situacao === 'ativa'
              ? 'Ela está no ar: salvar cria uma versão em teste, e a de agora continua rodando até alguém ativar a nova.'
              : 'Salvar cria uma versão nova, em rascunho: ela só roda depois de ir para teste e ser ativada.'}
        </p>

        <Campo
          rotulo="Nome"
          ajuda={
            edicao
              ? 'Trocar o nome não cria versão.'
              : 'Opcional. Sem nome, ela leva o nome do modelo.'
          }
          placeholder={modelo.nome}
          value={nome}
          maxLength={120}
          onChange={(e) => definirNome(e.target.value)}
        />

        {modelo.chave === MODELO_REDISTRIBUIR && (
          <CamposRedistribuir
            f={redistribuir}
            definir={definirRedistribuir}
            problemas={problemas}
          />
        )}
        {modelo.chave === MODELO_RESPONSAVEL && (
          <p className="configurar-modelo__ajuda">Este modelo não pede configuração.</p>
        )}
        {modelo.chave === MODELO_MENU && (
          <CamposMenu
            f={menu}
            definir={definirMenu}
            problemas={problemas}
            acoes={acoes}
            acoesFalharam={blocos.isError}
            aoTentarDeNovo={() => void blocos.refetch()}
          />
        )}
        {modelo.chave === MODELO_LEMBRETE && (
          <>
            <CamposDaAgenda
              agenda={lembrete.agenda}
              definir={(agenda) => definirLembrete({ ...lembrete, agenda })}
              problemas={problemas}
              titulo="Quando ela lê a agenda"
            />
            <SeletorDeOperacao
              operacoes={consultas}
              valor={lembrete.operacaoId}
              definir={(operacaoId) => definirLembrete({ ...lembrete, operacaoId })}
              rotulo="A consulta que traz os compromissos"
              erro={problemas['operacao']}
            />
            <Campo
              rotulo="Modelo de mensagem do lembrete"
              ajuda="O nome do modelo aprovado no WhatsApp, como está no Gerenciador da Meta."
              value={lembrete.modeloDoLembrete}
              maxLength={200}
              onChange={(e) => definirLembrete({ ...lembrete, modeloDoLembrete: e.target.value })}
              {...(problemas['modeloDoLembrete'] ? { erro: problemas['modeloDoLembrete'] } : {})}
            />
          </>
        )}
        {modelo.chave === MODELO_ROTINA && (
          <>
            <CamposDaAgenda
              agenda={rotina.agenda}
              definir={(agenda) => definirRotina({ ...rotina, agenda })}
              problemas={problemas}
              titulo="Quando ela roda"
            />
            <SeletorDeOperacao
              operacoes={consultas}
              valor={rotina.operacaoId}
              comParametros
              definir={(operacaoId) =>
                definirRotina({
                  ...rotina,
                  operacaoId,
                  parametrosDaOperacao: (
                    consultas.data?.itens.find((o) => o.id === operacaoId)?.parametros ?? []
                  ).map(() => ''),
                  // As colunas mudam com a consulta: o que foi montado com as de antes recomeça.
                  parametros: {},
                })
              }
              rotulo="A consulta cujas linhas viram ações"
              erro={problemas['operacao']}
            />
            {consultaDaRotina && (
              <ParametrosDaRotina
                parametros={consultaDaRotina.parametros}
                valores={rotina.parametrosDaOperacao}
                problemas={problemas}
                aoMudar={(parametrosDaOperacao) =>
                  definirRotina({ ...rotina, parametrosDaOperacao })
                }
              />
            )}
            {blocos.isError && (
              <Faixa
                tom="bloqueado"
                acao={{ rotulo: 'Tentar de novo', aoClicar: () => void blocos.refetch() }}
              >
                Não foi possível ler as ações agora.
              </Faixa>
            )}
            <CampoSelecao
              rotulo="O que fazer com cada linha"
              value={rotina.acao}
              onChange={(e) => definirRotina({ ...rotina, acao: e.target.value, parametros: {} })}
              {...(problemas['acao'] ? { erro: problemas['acao'] } : {})}
            >
              <option value="">Escolha uma ação</option>
              {acoes.map((a) => (
                <option key={a.acao} value={a.acao}>
                  {a.titulo}
                </option>
              ))}
            </CampoSelecao>
            {parametrosDaAcao.length > 0 && (
              <fieldset className="configurar-modelo__grupo">
                <legend>Os dados da ação</legend>
                {parametrosDaAcao.map((p) => (
                  <DadoDaAcao
                    key={`${rotina.operacaoId}:${rotina.acao}:${p.nome}`}
                    acao={rotina.acao}
                    parametro={p}
                    colunas={colunasDaRotina}
                    valor={rotina.parametros[p.nome] ?? ''}
                    aoMudar={(valor) =>
                      definirRotina({
                        ...rotina,
                        parametros: { ...rotina.parametros, [p.nome]: valor },
                      })
                    }
                    {...(problemas[`parametro.${p.nome}`]
                      ? { erro: problemas[`parametro.${p.nome}`]! }
                      : {})}
                  />
                ))}
              </fieldset>
            )}
          </>
        )}

        {quantos > 0 && (
          <Faixa tom="bloqueado">
            {quantos === 1
              ? `Falta acertar 1 campo para ${edicao ? 'salvar' : 'criar'}.`
              : `Faltam acertar ${quantos} campos para ${edicao ? 'salvar' : 'criar'}.`}
          </Faixa>
        )}
        {editar.isError && soONomeFalhou && (
          <Faixa tom="bloqueado">
            A configuração foi salva, mas o nome não. Feche e troque o nome de novo.
          </Faixa>
        )}
        {editar.isError && !soONomeFalhou && (
          <Faixa tom="bloqueado">
            {recadoDeErro(editar.error, {
              generica: 'Não foi possível salvar agora. Nada mudou; tente de novo.',
              porCodigo: {
                ENGINE_INDISPONIVEL:
                  'O serviço das automações não respondeu. Nada mudou; tente de novo daqui a pouco.',
                CONFLITO_DE_VERSAO:
                  'Outra pessoa mudou esta automação enquanto você editava. Feche, abra de novo e refaça a mudança.',
              },
            })}
          </Faixa>
        )}
        {criar.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(criar.error, {
              generica: 'Não foi possível criar agora. Nada foi criado; tente de novo.',
              porCodigo: {
                ENGINE_INDISPONIVEL:
                  'O serviço das automações não respondeu. Nada foi criado; tente de novo daqui a pouco.',
                IDEMPOTENCIA_DIVERGENTE:
                  'Ela pode já ter sido criada no envio anterior. Confira em Minhas automações antes de criar de novo.',
              },
            })}
          </Faixa>
        )}
      </form>
    </Dialogo>
  );
}

function CamposRedistribuir({
  f,
  definir,
  problemas,
}: {
  f: FormularioRedistribuir;
  definir: (f: FormularioRedistribuir) => void;
  problemas: Problemas;
}): ReactNode {
  const mudarDia = (i: number, mudanca: Partial<FormularioRedistribuir['dias'][number]>): void =>
    definir({ ...f, dias: f.dias.map((d, j) => (j === i ? { ...d, ...mudanca } : d)) });

  return (
    <>
      <CampoDeDuracao
        rotulo="Prazo da primeira resposta"
        ajuda="Conta só dentro do expediente. Passou disso sem resposta, a conversa vai para outra pessoa. Os segundos contam como o minuto seguinte."
        valorMs={msDosMinutos(f.minutos)}
        aoMudar={(ms) => definir({ ...f, minutos: minutosDoMs(ms) })}
        {...(problemas['minutos'] ? { erro: problemas['minutos'] } : {})}
      />

      <fieldset className="configurar-modelo__grupo">
        <legend>Expediente</legend>
        <CampoSelecao
          rotulo="Fuso"
          value={f.fuso}
          onChange={(e) => definir({ ...f, fuso: e.target.value })}
        >
          {FUSOS.map((z) => (
            <option key={z.valor} value={z.valor}>
              {z.rotulo}
            </option>
          ))}
        </CampoSelecao>
        <p className="configurar-modelo__ajuda">Para ir até a meia-noite, termine às 00:00.</p>
        <ul className="configurar-modelo__dias">
          {f.dias.map((d, i) => (
            <li key={DIAS_DA_SEMANA[i]} className="configurar-modelo__dia">
              <label className="configurar-modelo__marcar">
                <input
                  type="checkbox"
                  checked={d.ligado}
                  onChange={(e) => mudarDia(i, { ligado: e.target.checked })}
                />
                {DIAS_DA_SEMANA[i]}
              </label>
              {d.ligado && (
                <div className="configurar-modelo__horas">
                  <Campo
                    rotuloOculto={`${DIAS_DA_SEMANA[i]}: começa às`}
                    type="time"
                    tamanho="miudo"
                    value={d.inicio}
                    onChange={(e) => mudarDia(i, { inicio: e.target.value })}
                    {...ligadoAoErro(problemas[`dias.${i}`], `erro-do-dia-${i}`)}
                  />
                  <span aria-hidden="true">até</span>
                  <Campo
                    rotuloOculto={`${DIAS_DA_SEMANA[i]}: termina às`}
                    type="time"
                    tamanho="miudo"
                    value={d.fim}
                    onChange={(e) => mudarDia(i, { fim: e.target.value })}
                    {...ligadoAoErro(problemas[`dias.${i}`], `erro-do-dia-${i}`)}
                  />
                </div>
              )}
              {problemas[`dias.${i}`] && (
                <p className="configurar-modelo__erro" role="alert" id={`erro-do-dia-${i}`}>
                  {problemas[`dias.${i}`]}
                </p>
              )}
            </li>
          ))}
        </ul>
        {problemas['dias'] && (
          <p className="configurar-modelo__erro" role="alert">
            {problemas['dias']}
          </p>
        )}
      </fieldset>

      <Campo
        rotulo="Modelo de mensagem para avisar o cliente (opcional)"
        ajuda="O nome do modelo aprovado no WhatsApp. Sem ele, o cliente não recebe aviso."
        value={f.modeloDaMensagem}
        maxLength={200}
        onChange={(e) => definir({ ...f, modeloDaMensagem: e.target.value })}
      />
    </>
  );
}

type AcaoDoCatalogo = {
  acao: string;
  titulo: string;
  parametros: readonly ParametroDaAcao[];
};

/**
 * O menu por canal: a mensagem com as opções na conversa nova, o lembrete e o
 * encerramento por falta de resposta. Cada opção dispara uma ação do CRM.
 */
function CamposMenu({
  f,
  definir,
  problemas,
  acoes,
  acoesFalharam,
  aoTentarDeNovo,
}: {
  f: FormularioMenu;
  definir: (f: FormularioMenu) => void;
  problemas: Problemas;
  acoes: readonly AcaoDoCatalogo[];
  acoesFalharam: boolean;
  aoTentarDeNovo: () => void;
}): ReactNode {
  const caixas = useCaixasDoAtendimento();
  const mudarOpcao = (i: number, mudanca: Partial<FormularioMenu['opcoes'][number]>): void =>
    definir({ ...f, opcoes: f.opcoes.map((o, j) => (j === i ? { ...o, ...mudanca } : o)) });
  const erro = (campo: string): { erro: string } | Record<string, never> =>
    problemas[campo] ? { erro: problemas[campo] } : {};

  return (
    <>
      <CampoSelecao
        rotulo="Canal"
        value={f.canalId}
        disabled={caixas.isPending}
        onChange={(e) => definir({ ...f, canalId: e.target.value })}
        ajuda="O menu aparece na conversa nova deste canal."
        {...erro('canal')}
      >
        <option value="">{caixas.isPending ? 'Carregando…' : 'Escolha o canal'}</option>
        {(caixas.data?.itens ?? []).map((c) => (
          <option key={c.referencia_externa} value={c.referencia_externa}>
            {c.nome ?? 'Canal sem nome'}
          </option>
        ))}
      </CampoSelecao>
      {caixas.isError && (
        <Faixa
          tom="bloqueado"
          acao={{ rotulo: 'Tentar de novo', aoClicar: () => void caixas.refetch() }}
        >
          Não foi possível ler os canais agora.
        </Faixa>
      )}

      <CampoArea
        rotulo="Mensagem do menu"
        ajuda="Vai junto com as opções, quando a conversa começa."
        rows={3}
        maxLength={1_024}
        value={f.textoDoMenu}
        onChange={(e) => definir({ ...f, textoDoMenu: e.target.value })}
        {...erro('textoDoMenu')}
      />

      <fieldset className="configurar-modelo__grupo">
        <legend>As opções</legend>
        {acoesFalharam && (
          <Faixa tom="bloqueado" acao={{ rotulo: 'Tentar de novo', aoClicar: aoTentarDeNovo }}>
            Não foi possível ler as ações agora.
          </Faixa>
        )}
        <ol className="configurar-modelo__opcoes">
          {f.opcoes.map((o, i) => {
            const parametros = acoes.find((a) => a.acao === o.acao)?.parametros ?? [];
            return (
              <li key={o.chave ?? i} className="configurar-modelo__opcao">
                <Campo
                  rotulo={`Opção ${i + 1}: nome`}
                  ajuda="O texto do botão, até 24 letras."
                  maxLength={24}
                  value={o.rotulo}
                  onChange={(e) => mudarOpcao(i, { rotulo: e.target.value })}
                  {...erro(`opcao.${i}.rotulo`)}
                />
                <CampoSelecao
                  rotulo={`Opção ${i + 1}: o que acontece`}
                  value={o.acao}
                  onChange={(e) => mudarOpcao(i, { acao: e.target.value, parametros: {} })}
                  {...erro(`opcao.${i}.acao`)}
                >
                  <option value="">Escolha uma ação</option>
                  {acoes.map((a) => (
                    <option key={a.acao} value={a.acao}>
                      {a.titulo}
                    </option>
                  ))}
                </CampoSelecao>
                {parametros.map((p) => (
                  <DadoDaAcao
                    key={`${o.acao}:${p.nome}`}
                    acao={o.acao}
                    parametro={p}
                    colunas={[]}
                    // A ação roda na conversa de quem escolheu a opção: o evento, não uma linha.
                    doEvento={DO_MENU}
                    rotulo={`Opção ${i + 1}: ${p.obrigatorio ? p.titulo : `${p.titulo} (opcional)`}`}
                    valor={o.parametros[p.nome] ?? ''}
                    aoMudar={(valor) =>
                      mudarOpcao(i, { parametros: { ...o.parametros, [p.nome]: valor } })
                    }
                    {...erro(`opcao.${i}.parametro.${p.nome}`)}
                  />
                ))}
                {f.opcoes.length > 1 && (
                  <div>
                    <Botao
                      variante="fantasma"
                      miudo
                      onClick={() => definir({ ...f, opcoes: f.opcoes.filter((_, j) => j !== i) })}
                    >
                      {`Tirar a opção ${i + 1}`}
                    </Botao>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
        {f.opcoes.length < MAXIMO_DE_OPCOES && (
          <div>
            <Botao
              variante="secundario"
              miudo
              onClick={() => definir({ ...f, opcoes: [...f.opcoes, opcaoVazia()] })}
            >
              Adicionar opção
            </Botao>
          </div>
        )}
      </fieldset>

      <fieldset className="configurar-modelo__grupo">
        <legend>Sem resposta</legend>
        <CampoArea
          rotulo="Lembrete"
          rows={2}
          maxLength={1_024}
          value={f.textoDoLembrete}
          onChange={(e) => definir({ ...f, textoDoLembrete: e.target.value })}
          {...erro('textoDoLembrete')}
        />
        <CampoDeDuracao
          rotulo="Lembrar depois de"
          valorMs={msDosMinutos(f.lembrarAposMinutos)}
          aoMudar={(ms) => definir({ ...f, lembrarAposMinutos: minutosDoMs(ms) })}
          {...erro('lembrar')}
        />
        <CampoDeDuracao
          rotulo="Encerrar depois de"
          valorMs={msDosMinutos(f.encerrarAposMinutos)}
          aoMudar={(ms) => definir({ ...f, encerrarAposMinutos: minutosDoMs(ms) })}
          {...erro('encerrar')}
        />
        <Campo
          rotulo="Motivo do encerramento"
          ajuda="Fica registrado na conversa encerrada."
          maxLength={280}
          value={f.motivoDoEncerramento}
          onChange={(e) => definir({ ...f, motivoDoEncerramento: e.target.value })}
          {...erro('motivo')}
        />
        <p className="configurar-modelo__ajuda">
          Quando alguém da equipe assume a conversa, o menu sai sozinho.
        </p>
      </fieldset>
    </>
  );
}

function CamposDaAgenda({
  agenda,
  definir,
  problemas,
  titulo,
}: {
  agenda: FormularioAgenda;
  definir: (agenda: FormularioAgenda) => void;
  problemas: Problemas;
  titulo: string;
}): ReactNode {
  const mudar = (i: number, mudanca: Partial<FormularioAgenda['horarios'][number]>): void =>
    definir({
      ...agenda,
      horarios: agenda.horarios.map((h, j) => (j === i ? { ...h, ...mudanca } : h)),
    });

  return (
    <fieldset className="configurar-modelo__grupo">
      <legend>{titulo}</legend>
      <CampoSelecao
        rotulo="Fuso"
        value={agenda.fuso}
        onChange={(e) => definir({ ...agenda, fuso: e.target.value })}
      >
        {FUSOS.map((z) => (
          <option key={z.valor} value={z.valor}>
            {z.rotulo}
          </option>
        ))}
      </CampoSelecao>
      <ul className="configurar-modelo__dias">
        {agenda.horarios.map((h, i) => (
          <li key={i} className="configurar-modelo__dia">
            <div className="configurar-modelo__horas">
              <CampoSelecao
                rotuloOculto={`Horário ${i + 1}: dia`}
                tamanho="miudo"
                value={h.dia}
                onChange={(e) => mudar(i, { dia: e.target.value })}
              >
                <option value="todo">Todo dia</option>
                {DIAS_DA_SEMANA.map((d, n) => (
                  <option key={d} value={String(n)}>
                    {d}
                  </option>
                ))}
              </CampoSelecao>
              <span aria-hidden="true">às</span>
              <Campo
                rotuloOculto={`Horário ${i + 1}: hora`}
                type="time"
                tamanho="miudo"
                value={h.hora}
                onChange={(e) => mudar(i, { hora: e.target.value })}
              />
              {agenda.horarios.length > 1 && (
                <Botao
                  variante="fantasma"
                  miudo
                  onClick={() =>
                    definir({ ...agenda, horarios: agenda.horarios.filter((_, j) => j !== i) })
                  }
                >
                  Tirar
                </Botao>
              )}
            </div>
            {problemas[`agenda.${i}`] && (
              <p className="configurar-modelo__erro" role="alert">
                {problemas[`agenda.${i}`]}
              </p>
            )}
          </li>
        ))}
      </ul>
      {agenda.horarios.length < 50 && (
        <div>
          <Botao
            variante="secundario"
            miudo
            onClick={() =>
              definir({ ...agenda, horarios: [...agenda.horarios, { dia: 'todo', hora: '08:00' }] })
            }
          >
            Adicionar horário
          </Botao>
        </div>
      )}
    </fieldset>
  );
}

/**
 * As consultas que um modelo pode ler: publicadas, de leitura e sem parâmetros.
 * O modelo não manda valores para os parâmetros, e uma consulta que os espera
 * só falharia quando a automação rodasse.
 */
function SeletorDeOperacao({
  valor,
  definir,
  rotulo,
  erro,
  comParametros = false,
  operacoes,
}: {
  valor: string;
  definir: (id: string) => void;
  rotulo: string;
  erro: string | undefined;
  /** A rotina aceita consulta que recebe valores (a data, um código); o lembrete, não. */
  comParametros?: boolean;
  /** A leitura das consultas, feita uma vez por quem monta o formulário. */
  operacoes: ReturnType<typeof useOperacoesDeDados>;
}): ReactNode {
  const usaveis = (operacoes.data?.itens ?? []).filter(
    (o) =>
      o.situacao === 'publicada' &&
      o.tipo === 'leitura' &&
      (comParametros || o.parametros.length === 0),
  );
  const tipoQueServe = comParametros
    ? 'publicada e de leitura'
    : 'publicada, de leitura e sem parâmetros';

  if (operacoes.isError) {
    return (
      <div className="configurar-modelo__falha">
        <Faixa
          tom="bloqueado"
          acao={{ rotulo: 'Tentar de novo', aoClicar: () => void operacoes.refetch() }}
        >
          Não foi possível ler as consultas agora.
        </Faixa>
        {erro && (
          <p className="configurar-modelo__erro" role="alert">
            {erro}
          </p>
        )}
      </div>
    );
  }

  return (
    <CampoSelecao
      rotulo={rotulo}
      value={valor}
      disabled={operacoes.isPending}
      onChange={(e) => definir(e.target.value)}
      ajuda={
        !operacoes.isPending && usaveis.length === 0
          ? `Nenhuma consulta serve ainda: publique uma consulta ${tipoQueServe} em Operações SQL, e volte aqui.`
          : `Só as consultas de finalidade Consulta, ${tipoQueServe}.`
      }
      {...(erro ? { erro } : {})}
    >
      <option value="">{operacoes.isPending ? 'Carregando…' : 'Escolha a consulta'}</option>
      {usaveis.map((o) => (
        <option key={o.id} value={o.id}>
          {o.nome}
        </option>
      ))}
    </CampoSelecao>
  );
}

/** O `aria-invalid` e a descrição do erro num campo que divide o erro com outro. */
function ligadoAoErro(
  erro: string | undefined,
  id: string,
): { 'aria-invalid'?: true; 'aria-describedby'?: string } {
  return erro ? { 'aria-invalid': true, 'aria-describedby': id } : {};
}

/*
 * Os campos da API (`configuracao.acao.parametros.etapa`) pelos nomes deste
 * formulário. O que não tem campo aqui fica só na faixa do erro.
 */
const CAMPO_DA_API: readonly [RegExp, (m: RegExpExecArray) => string][] = [
  [
    /^configuracao\.opcoes\.(\d+)\.acoes\.\d+\.parametros\.([\w-]+)/,
    (m) => `opcao.${m[1]}.parametro.${m[2]}`,
  ],
  [/^configuracao\.opcoes\.(\d+)\.acoes/, (m) => `opcao.${m[1]}.acao`],
  [/^configuracao\.opcoes\.(\d+)\.rotulo/, (m) => `opcao.${m[1]}.rotulo`],
  [/^configuracao\.canal_id/, () => 'canal'],
  [/^configuracao\.texto_do_menu/, () => 'textoDoMenu'],
  [/^configuracao\.texto_do_lembrete/, () => 'textoDoLembrete'],
  [/^configuracao\.lembrar_apos_minutos/, () => 'lembrar'],
  [/^configuracao\.encerrar_apos_minutos/, () => 'encerrar'],
  [/^configuracao\.motivo_do_encerramento/, () => 'motivo'],
  [/^configuracao\.acao\.parametros\.([\w-]+)/, (m) => `parametro.${m[1]}`],
  [/^configuracao\.acao(\.acao)?$/, () => 'acao'],
  [/^configuracao\.operacao_id/, () => 'operacao'],
  [/^configuracao\.minutos_sem_resposta/, () => 'minutos'],
  [/^configuracao\.modelo_do_lembrete/, () => 'modeloDoLembrete'],
  [/^configuracao\.agenda\.horarios\.(\d+)/, (m) => `agenda.${m[1]}`],
];

function problemasDaApi(erro: ErroDaApi): Problemas {
  const p: Problemas = {};
  for (const e of erro.problema?.errors ?? []) {
    for (const [padrao, campo] of CAMPO_DA_API) {
      const m = padrao.exec(e.campo);
      if (m) {
        p[campo(m)] = e.problema;
        break;
      }
    }
  }
  return p;
}
